import classNames from 'classnames';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import React, {useState} from 'react';

import {CoursePriceStore} from '@udemy/common-stores';
import {TrackImpression} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {Loader} from '@udemy/react-reveal-components';
import {udUserAgnosticTrackingParams} from '@udemy/shared-utils';
import {ShoppingClient, CouponStore} from '@udemy/shopping';
import {
    getSmartBarStore,
    BasicBarContentWithTruncation,
    SMART_BAR_NOTICE_TYPE,
} from '@udemy/smart-bar';
import {useStores} from '@udemy/store-provider';
import {UDDataUserAgnosticTrackingParams, useUDData} from '@udemy/ud-data';

import styles from './cart-opt-in.module.less';

export interface OptInProps {
    couponStore: CouponStore;
}

export const CartOptIn: React.FC<OptInProps> = observer(({couponStore}) => {
    const {interpolate, gettext} = useI18n();
    const {Config: configData, me: udMe, request: requestData} = useUDData();
    const [smartBarStore, setSmartBarStore] = useState<ReturnType<typeof getSmartBarStore> | null>(
        null,
    );

    const [coursePriceStore] = useStores([CoursePriceStore]);
    if (smartBarStore === null) {
        setSmartBarStore(
            getSmartBarStore({
                params: globalThis.URLSearchParams
                    ? Object.fromEntries(new URLSearchParams(window.location?.search).entries())
                    : {},
                forceNoticeType: SMART_BAR_NOTICE_TYPE,
                udConfig: configData,
                udRequest: requestData,
                udMe: udMe,
                userAgnosticTrackingParams:
                    udUserAgnosticTrackingParams() as UDDataUserAgnosticTrackingParams,
                gettext,
            }),
        );
    }

    const optInApplied = () => {
        return smartBarStore?.optInJustApplied();
    };

    const optInApplying = () => {
        return smartBarStore?.optInApplying();
    };

    const optInOnClick = () => {
        if (smartBarStore?.optInNotApplied()) {
            smartBarStore?.optIn().then(() => {
                runInAction(() => {
                    ShoppingClient.fetch().then(() => {
                        couponStore._updateState();
                        smartBarStore?.optedIn();
                        smartBarStore?.sendDealOptInEvent();
                    });
                    coursePriceStore.reloadCourses();
                });
            });
        }
    };

    if (!smartBarStore?.state.loaded) {
        return null;
    }

    const smartBarCtaText = smartBarStore.data.get('cta_text');
    const smartBarCtaAppliedText = smartBarStore.data.get('cta_applied_text');
    const ctaButtonText = interpolate(
        gettext('%(ctaText)s'),
        {
            ctaText: smartBarCtaText,
        },
        true,
    );
    const ctaAppliedButtonText = interpolate(
        gettext('%(ctaAppliedText)s'),
        {
            ctaAppliedText: smartBarCtaAppliedText,
        },
        true,
    );

    const sideBar = (
        <div className={classNames(styles['cart-opt-in'])}>
            <BasicBarContentWithTruncation
                className={styles['cart-opt-in__content']}
                data={{
                    title: smartBarStore.data.get('title'),
                    subtitle: smartBarStore.data.get('subtitle'),
                    action_url: smartBarStore.data.get('action_url'),
                    enableTimer: smartBarStore.data.get('enable_timer'),
                    endTime: smartBarStore.membership.get('end_time'),
                    shouldShowSeconds: smartBarStore.data.get('enable_seconds_in_timer'),
                    daysToShowTimer: smartBarStore.data.get('days_to_show_timer'),
                }}
                onLinkClick={(url, location) => smartBarStore.sendClickEvent(url, location)}
                onActionURLClick={() => {
                    const actionUrl = smartBarStore.data.get('action_url');
                    if (actionUrl) {
                        smartBarStore.sendClickEvent(actionUrl);
                    }
                }}
                hasCta={true}
            />
            <div>
                <Button
                    udStyle="ghost"
                    size="medium"
                    className={classNames(styles['opt-in-cta-button'], {
                        'ud-link-underline': smartBarStore.optInNotApplied(),
                    })}
                    onClick={optInOnClick}
                >
                    {optInApplied() ? (
                        ctaAppliedButtonText
                    ) : optInApplying() ? (
                        <Loader color="inherit" overlay={true} className="loader" />
                    ) : (
                        ctaButtonText
                    )}
                </Button>
            </div>
        </div>
    );

    return (
        <>
            {smartBarStore.noticeType?.showOptIn && !smartBarStore.membership.get('opt_in') && (
                <TrackImpression
                    trackFunc={() => {
                        smartBarStore.sendImpressionsEvent();
                    }}
                >
                    <div>{sideBar}</div>
                </TrackImpression>
            )}
        </>
    );
});
